import { useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { fileToText } from "../lib/format";
import type { Contact } from "../lib/types";
import { ContactSearchList } from "./ContactSearchList";
import { TagInput } from "./TagInput";

const DOMAIN_RE =
  /[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+/gi;

function extractDomainsFromText(text: string): string[] {
  const matches = text.toLowerCase().match(DOMAIN_RE) ?? [];
  return matches.filter((m) => /\.[a-z]{2,}$/.test(m));
}

function extractDomainsFromJson(text: string): string[] {
  const data = JSON.parse(text);
  if (!Array.isArray(data)) throw new Error("Expected a JSON array.");
  const out: string[] = [];
  for (const item of data) {
    if (typeof item === "string") out.push(item);
    else if (item && typeof item === "object" && typeof item.domain === "string")
      out.push(item.domain);
  }
  return out;
}

export function ExampleDomains({
  label,
  color,
  values,
  onChange,
  notes,
  onNotesChange,
}: {
  label: string;
  color: string;
  values: string[];
  onChange: (next: string[]) => void;
  notes?: Record<string, string>;
  onNotesChange?: (next: Record<string, string>) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [pickerOpen, setPickerOpen] = useState(false);
  const csvRef = useRef<HTMLInputElement>(null);
  const jsonRef = useRef<HTMLInputElement>(null);

  const lower = new Set(values.map((v) => v.toLowerCase()));
  const collectReason = Boolean(onNotesChange);

  const addDomains = (domains: string[]): number => {
    const seen = new Set(lower);
    const merged = [...values];
    let added = 0;
    for (const d of domains) {
      const norm = d.trim().toLowerCase();
      if (norm && !seen.has(norm)) {
        seen.add(norm);
        merged.push(norm);
        added += 1;
      }
    }
    if (added) onChange(merged);
    return added;
  };

  const importFile = async (
    file: File | undefined,
    kind: "CSV" | "JSON",
    parse: (text: string) => string[],
  ) => {
    if (!file) return;
    try {
      const added = addDomains(parse(await fileToText(file)));
      enqueueSnackbar(`Imported ${added} domain${added === 1 ? "" : "s"} from ${kind}.`, {
        variant: added ? "success" : "info",
      });
    } catch (err) {
      enqueueSnackbar(
        `${kind} import failed: ${err instanceof Error ? err.message : String(err)}`,
        { variant: "error" },
      );
    }
  };

  return (
    <div className="field" style={{ flex: 1 }}>
      <label style={{ color }}>
        {label} ({values.length})
      </label>
      <TagInput values={values} onChange={onChange} placeholder="e.g. acme.com" />

      {collectReason && values.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <p className="card-desc" style={{ fontSize: 11, marginBottom: 4 }}>
            Reason per domain (optional — shown to the LLM as
            <em> why this lead is a bad fit</em>):
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {values.map((domain) => (
              <div
                key={domain}
                style={{
                  display: "grid",
                  gridTemplateColumns: "180px 1fr",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <code
                  style={{
                    fontSize: 11,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={domain}
                >
                  {domain}
                </code>
                <input
                  type="text"
                  value={notes?.[domain] ?? ""}
                  onChange={(e) =>
                    onNotesChange?.({
                      ...(notes ?? {}),
                      [domain]: e.target.value,
                    })
                  }
                  placeholder="Why is this a bad fit?"
                  style={{ width: "100%", fontSize: 12, minWidth: 0 }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" onClick={() => setPickerOpen((v) => !v)}>
          {pickerOpen ? "Close picker" : "Add from contacts"}
        </button>
        <button type="button" onClick={() => csvRef.current?.click()}>
          Import CSV
        </button>
        <button type="button" onClick={() => jsonRef.current?.click()}>
          Import JSON
        </button>
        <input
          ref={csvRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={(e) => {
            void importFile(e.target.files?.[0], "CSV", extractDomainsFromText);
            e.target.value = "";
          }}
        />
        <input
          ref={jsonRef}
          type="file"
          accept=".json,application/json"
          style={{ display: "none" }}
          onChange={(e) => {
            void importFile(e.target.files?.[0], "JSON", extractDomainsFromJson);
            e.target.value = "";
          }}
        />
      </div>

      {pickerOpen && (
        <div style={{ marginTop: 8 }}>
          <ContactSearchList
            itemOpacity={(c: Contact) =>
              c.domain && lower.has(c.domain.toLowerCase()) ? 0.5 : 1
            }
            itemAction={(c: Contact) => {
              const already = Boolean(c.domain && lower.has(c.domain.toLowerCase()));
              return (
                <button
                  type="button"
                  disabled={!c.domain || already}
                  onClick={() => {
                    if (c.domain && addDomains([c.domain])) {
                      enqueueSnackbar(`Added ${c.domain}.`, { variant: "success" });
                    }
                  }}
                >
                  {already ? "Added" : "Add"}
                </button>
              );
            }}
          />
        </div>
      )}
    </div>
  );
}
